export type RecruitOnboardingEmailType =
  | 'partial_onboarding'
  | 'full_onboarding'
  | 'nmls_clearance';

export type RecruitOnboardingDeliveryStatus =
  | 'pending'
  | 'processing'
  | 'sent'
  | 'failed'
  | 'suppressed'
  | 'cancelled';

export interface RecruitEmailDelivery {
  id: string;
  email_type: RecruitOnboardingEmailType;
  status: RecruitOnboardingDeliveryStatus;
  recipient_email: string | null;
  trigger_source: 'manual' | 'automatic' | 'legacy_import';
  requested_by: string | null;
  requested_by_name?: string | null;
  attempt_count: number;
  provider_message_id: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  last_attempt_at: string | null;
  sent_at: string | null;
}

export type RecruitOnboardingAccessMode = 'provisioned' | 'existing';

export interface RecruitEmailStageSource {
  status: string;
  personalEmail: string;
  companyEmail?: string | null;
  gmailTempPassword?: string | null;
  linkedUserId?: string | null;
  optimalUsername?: string | null;
  encompassUserId?: string | null;
  encompassTempPassword?: string | null;
  accessMode?: RecruitOnboardingAccessMode | string | null;
  /** Encompass-specific provenance: 'existing' waives the Encompass temp
   *  password; 'provisioned'/legacy require it even in overall existing mode. */
  encompassAccessMode?: RecruitOnboardingAccessMode | string | null;
  nmlsAuthorizationDate?: string | null;
  deliveries: RecruitEmailDelivery[];
}

export type RecruitEmailStageAction =
  | 'initial_invitation'
  | 'send'
  | 'retry'
  | 'resend'
  | 'none';

export interface RecruitEmailStage {
  state: 'initial_invitation' | 'blocked' | 'ready' | 'processing' | 'sent';
  action: RecruitEmailStageAction;
  emailType?: RecruitOnboardingEmailType;
  deliveryId?: string;
  buttonText: string;
  description: string;
  recipientEmail?: string;
  missingCredentials: string[];
  delivery?: RecruitEmailDelivery;
  /** Whether the credential email will carry a temp password for each system;
   *  false renders the "you already have access" copy instead. */
  includesGmailTempPassword: boolean;
  includesEncompassTempPassword: boolean;
}

const EMAIL_LABEL: Record<RecruitOnboardingEmailType, string> = {
  partial_onboarding: 'Partial Onboarding',
  full_onboarding: 'Full Onboarding',
  nmls_clearance: 'NMLS Clearance'
};

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function missingCredentials(source: RecruitEmailStageSource): string[] {
  // Temp passwords are waived PER SYSTEM, only where access provably predates
  // onboarding. Gmail access comes with the link itself (overall 'existing'
  // mode); Encompass has its own provenance because an existing recruit can
  // still receive a newly provisioned Encompass account.
  const requiresGmailTempPassword = source.accessMode !== 'existing';
  const requiresEncompassTempPassword = source.encompassAccessMode !== 'existing';
  const checks: Array<[string, unknown]> = [
    ['Gmail account', source.companyEmail],
    ...(requiresGmailTempPassword
      ? [['Gmail temporary password', source.gmailTempPassword] as [string, unknown]]
      : []),
    ['Portal user account', source.linkedUserId],
    ['Optimal Blue username', source.optimalUsername],
    ['Encompass user ID', source.encompassUserId],
    ...(requiresEncompassTempPassword
      ? [['Encompass temporary password', source.encompassTempPassword] as [string, unknown]]
      : [])
  ];

  return checks.flatMap(([label, value]) => hasText(value) ? [] : [label]);
}

function newest(deliveries: RecruitEmailDelivery[]): RecruitEmailDelivery | undefined {
  return [...deliveries].sort((left, right) =>
    new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  )[0];
}

function isStaleProcessing(
  delivery: RecruitEmailDelivery,
  now: Date
): boolean {
  if (delivery.status !== 'processing' || !delivery.last_attempt_at) {
    return false;
  }
  const attemptedAt = new Date(delivery.last_attempt_at).getTime();
  return Number.isFinite(attemptedAt)
    && now.getTime() - attemptedAt >= 5 * 60 * 1000;
}

type RecruitEmailStageCore = Omit<RecruitEmailStage, 'includesGmailTempPassword' | 'includesEncompassTempPassword'>;

function stageForDelivery(
  emailType: RecruitOnboardingEmailType,
  delivery: RecruitEmailDelivery | undefined,
  recipientEmail: string,
  now: Date
): RecruitEmailStageCore {
  const label = EMAIL_LABEL[emailType];
  if (!delivery || delivery.status === 'cancelled') {
    return {
      state: 'ready',
      action: 'send',
      emailType,
      buttonText: `Send ${label} Email`,
      description: `Ready to send the ${label.toLowerCase()} email.`,
      recipientEmail,
      missingCredentials: []
    };
  }
  if (delivery.status === 'sent') {
    return {
      state: 'sent',
      action: 'resend',
      emailType,
      deliveryId: delivery.id,
      buttonText: `${label} Sent`,
      description: delivery.sent_at
        ? `Delivered ${new Date(delivery.sent_at).toLocaleString()}.`
        : 'Delivered successfully.',
      recipientEmail,
      missingCredentials: [],
      delivery
    };
  }
  if (delivery.status === 'processing' && !isStaleProcessing(delivery, now)) {
    return {
      state: 'processing',
      action: 'none',
      emailType,
      deliveryId: delivery.id,
      buttonText: 'Sending…',
      description: `${label} delivery is in progress.`,
      recipientEmail,
      missingCredentials: [],
      delivery
    };
  }

  return {
    state: 'ready',
    action: 'retry',
    emailType,
    deliveryId: delivery.id,
    buttonText: `Retry ${label} Email`,
    description: delivery.status === 'suppressed'
      ? `${label} is disabled in Company Settings. Re-enable it, then retry this delivery.`
      : delivery.status === 'processing'
        ? 'The previous attempt stopped responding and can be retried safely.'
        : delivery.error_message || 'The previous attempt did not complete.',
    recipientEmail,
    missingCredentials: [],
    delivery
  };
}

export function resolveRecruitEmailStage(
  source: RecruitEmailStageSource,
  now = new Date()
): RecruitEmailStage {
  return {
    ...resolveStageCore(source, now),
    includesGmailTempPassword: hasText(source.gmailTempPassword),
    includesEncompassTempPassword: hasText(source.encompassTempPassword)
  };
}

function resolveStageCore(
  source: RecruitEmailStageSource,
  now: Date
): RecruitEmailStageCore {
  if (source.status !== 'Approved') {
    return {
      state: 'initial_invitation',
      action: 'initial_invitation',
      buttonText: 'Send Invitation Email',
      description: 'Send the recruiting invitation and agreement workflow.',
      recipientEmail: source.personalEmail,
      missingCredentials: []
    };
  }

  const missing = missingCredentials(source);
  const authorized = hasText(source.nmlsAuthorizationDate);
  if (missing.length > 0) {
    return {
      state: 'blocked',
      action: 'none',
      buttonText: authorized ? 'Waiting for System Setup' : 'Complete System Setup',
      description: `Missing: ${missing.join(', ')}.`,
      missingCredentials: missing
    };
  }

  const deliveredCredentials = source.deliveries.some((delivery) =>
    delivery.status === 'sent'
      && (delivery.email_type === 'partial_onboarding'
        || delivery.email_type === 'full_onboarding')
  );

  if (!authorized) {
    const partial = newest(source.deliveries.filter((delivery) =>
      delivery.email_type === 'partial_onboarding'
        && delivery.status !== 'cancelled'
    ));
    if (partial) {
      return stageForDelivery(
        'partial_onboarding',
        partial,
        source.personalEmail,
        now
      );
    }

    if (deliveredCredentials) {
      const credentialDelivery = newest(source.deliveries.filter((delivery) =>
        delivery.status === 'sent'
          && (delivery.email_type === 'partial_onboarding'
            || delivery.email_type === 'full_onboarding')
      ));
      return {
        state: 'sent',
        action: 'none',
        emailType: credentialDelivery?.email_type,
        deliveryId: credentialDelivery?.id,
        buttonText: 'System Credentials Sent',
        description: 'Credentials were delivered previously. Marketing remains locked until NMLS authorization is recorded.',
        recipientEmail: credentialDelivery?.recipient_email || source.personalEmail,
        missingCredentials: [],
        delivery: credentialDelivery
      };
    }

    return stageForDelivery(
      'partial_onboarding',
      undefined,
      source.personalEmail,
      now
    );
  }

  const desiredType: RecruitOnboardingEmailType = deliveredCredentials
    ? 'nmls_clearance'
    : 'full_onboarding';
  const desiredDelivery = newest(source.deliveries.filter((delivery) =>
    delivery.email_type === desiredType && delivery.status !== 'cancelled'
  ));
  const recipient = desiredType === 'nmls_clearance'
    ? source.companyEmail || ''
    : source.personalEmail;
  return stageForDelivery(desiredType, desiredDelivery, recipient, now);
}
